import inlineCTA from "assets/images/psp/inlineCTA.jpg";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import portableStorage from "assets/images/psp/portable-storage.webp";
import relocatingStorage from "assets/images/psp/relocating-storage.webp";
import movingSeason from "assets/images/psp/moving-season.webp";
import portableStorageConsiderations from "assets/images/psp/Portable-storage-considerations.webp";
import HeaderBackgroundDesktop from "assets/images/psp/hero/portable-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/portable-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/portable-header-tablet.webp";
import { storageCategories } from "../utils/getPSPLinks";

const portablePriceConsiderationsRichContent = `
**Location:** Urban areas typically cost 20-40% more than rural locations

**Duration:** Longer rentals often include discounted monthly rates

**Distance:** Moving services are priced by mileage for long-distance transport

**Season:** Summer months (peak moving season) see 15-25% price increases

**Additional Services:** Packing assistance, loading help, or insurance coverage
`;

const faqs = [
	{
		question: "How long can I keep a portable storage container?",
		answer: "Most companies offer flexible rental periods from one month to several years, making portable storage containers ideal for both short-term projects and long-term storage needs.\n\nFor local storage, you can typically keep the container on your property for 30-90 days, depending on local zoning regulations and HOA rules.\n\nIf you need longer storage, the container can be moved to a secure facility where monthly rates often decrease for extended commitments. Many providers offer discounts for rentals exceeding six months.\n\nFor moving purposes, companies usually allow 3-7 days for loading before transportation. This flexibility makes portable storage perfect for renovation projects, staging homes for sale, or managing complex relocations where timing is uncertain."
	},
	{
		question: "Are portable storage containers secure and weatherproof?",
		answer: "Yes, [quality portable storage containers](https://www.storage.com/blog/ubox-vs-pods-portable-storage-unit-guide/) feature heavy-duty steel construction with weatherproof seals that protect contents from rain, snow, and moisture damage.\n\nMost units sit several inches off the ground to prevent flooding and include ventilation systems to minimize condensation buildup.\n\nSecurity features include reinforced door latches, heavy-duty hinges, and the ability to use your lock for maximum control.\n\nWhen stored at company facilities, containers benefit from additional security measures like gated access, surveillance cameras, and on-site management.\n\nHowever, verify your homeowner's or renter's insurance coverage, as policies for portable storage may differ from traditional storage facilities.\n\nFor high-value items, consider additional insurance coverage offered by storage companies."
	},
	{
		question: "What items cannot be stored in portable storage containers?",
		answer: "[Like traditional storage, portable storage containers prohibit hazardous materials](https://www.storage.com/blog/what-cant-i-store-in-a-storage-unit/), including paint, gasoline, propane, chemicals, and batteries. Perishable foods, plants, and living organisms cannot be stored.\n\nMost companies restrict valuable items like jewelry, cash, important documents, and family heirlooms – keep these with you during moves.\n\nFirearms policies vary by company and state regulations, so verify specific restrictions.\n\nSome providers limit certain furniture types or require professional packing for extremely fragile items. Items requiring climate control (like wine, photographs, or musical instruments) may not be suitable for standard containers in extreme climates.\n\nAlways review your contract's prohibited items list thoroughly, as violations can void insurance coverage and result in disposal fees for restricted materials."
	},
	{
		question: "How do I prepare for portable storage container delivery?",
		answer: "Successful delivery requires proper site preparation to ensure safe placement and access. You'll need a level, stable surface at least 12 feet wide and 40+ feet long for the delivery truck to maneuver safely.\n\nClear any low-hanging branches and ensure overhead clearance of at least 15 feet for the delivery truck's hydraulic system. The ground must support significant weight – a loaded container can weigh 15,000+ pounds or more.\n\nMark underground utilities, sprinkler systems, and septic tanks to avoid damage during placement. Consider protecting driveways, lawns, or decorative surfaces with plywood if you're concerned about tire marks or ground pressure.\n\nSome storage containers with wheels offer more placement flexibility, but standard containers still require truck accessibility from the street. Contact your HOA if applicable, as some communities require permits for temporary container placement."
	},
	{
		question: "Can portable storage containers be moved between locations?",
		answer: 'Absolutely – this mobility is the key advantage of choosing portable storage over traditional units.\n\nMost companies can transport your loaded container locally, across state lines, or even coast-to-coast, making them perfect for long-distance moves or multi-stage relocations.\n\nLocal transportation within the same metro area typically costs $75-200, while long-distance moves can add $800-2,500+, depending on distance and logistics.\n\nSome providers offer "storage in transit" services, securely holding your container at their facility if the timing between locations doesn\'t align perfectly.\n\nWhen planning multiple moves, book transportation early as scheduling depends on truck availability and route optimization. Be aware of weight restrictions – overloaded containers may require partial unloading before transport.\n\nThis flexibility makes portable storage ideal for military families, corporate relocations, or anyone facing complex moving situations.'
	}
];

const relatedContentCards = [
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/5-ways-to-use-portable-storage-units/",
		date: "December 23, 2025",
		image: portableStorage,
		content:
			"Portable Storage Units: 5 Ways to Use Portable Storage Containers"
	},
	{
		category: "Moving Advice",
		url: "https://www.storage.com/blog/relocating-use-self-storage/",
		date: "October 15, 2025",
		image: relocatingStorage,
		content: "Relocating? Use Self Storage While You Find a New Home"
	},
	{
		category: "Moving Advice",
		url: "https://www.storage.com/blog/moving-season-and-self-storage/",
		date: "December 23, 2025",
		image: movingSeason,
		content: "Moving Season and Self Storage"
	}
];

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: "The person from Storage.com who assisted me was super helpful and offered several options. I saved a lot of money because of the great deals your company could offer, which were below market rate if I had dealt directly with the storage company.",
			author: "Pasadena Garden Club",
			title: "The person who assisted me was super…",
			imageSrc: GeorgeRansom
		},
		{
			body: "Abby, the customer service representative at storage.com, was professional, helpful and friendly. She found me a storage space at an excellent price in close proximity to my home. I can't thank her enough.",
			author: "Ann Alarcon",
			title: "A wonderful experience",
			imageSrc: AnnAlarcon
		},
		{
			body: "It was very easy to find storage close to me, and the selection was all mine. I was very happy with this process.",
			author: "Dale Howard",
			title: "Finding Storage",
			imageSrc: SMurphy
		}
	]
};

const portableHerodescription = `
Moving is stressful enough without making countless trips to a storage facility. [Renovating your home](https://storage.com/blog/self-storage-and-home-renovations/) while juggling work and family? The last thing you need is another complicated logistics challenge. A portable storage container eliminates the back-and-forth headaches by bringing secure, [weatherproof storage](https://www.storage.com/climate-controlled/) directly to your doorstep.\n\n
Whether you're navigating a major life change, tackling that long-overdue home project, or simply need reliable storage that works around your schedule, these mobile units give you one less thing to worry about. Let Storage.com help you reclaim your time and peace of mind with the right portable storage solution.
`;

const whatToExpectDescription = `
**Small Portable Storage Containers**

- Common Fees:
  - *Monthly Storage:* $149–$199  
  - *Delivery & Pickup:* $99–$149  
  - *Moving Service Fees:* $399–$599

**Medium Portable Storage Containers**
- Common Fees:
  - *Monthly Storage:* $199–$289  
  - *Delivery & Pickup:* $149–$199  
  - *Moving Service Fees:* $599–$899

**Large Portable Storage Containers**
- Common Fees:
  - *Monthly Storage:* $289–$399  
  - *Delivery & Pickup:* $199–$299  
  - *Moving Service Fees:* $799–$1,299
`;

export const PSPPortableContent = {
	type: "Portable",
	sections: [
		{
			title: "Portable Storage hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Portable Storage Units",
						description: portableHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Find Portable Storage",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Portable Storage Hero Image"
					}
				}
			]
		},
		{
			title: "Portable Storage Benefits",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "Why Choose Portable Storage?",
						background: "white",
						description:
							"From [furniture storage](https://storage.com/blog/how-to-store-furniture/) to full house contents, portable storage hits the sweet spot of convenience and security. Here's why you may want to choose portable storage over [traditional storage units](https://www.storage.com/self-storage/).",
						storages: [
							{
								title: "Convenience",
								bestFor: [
									"No commute",
									"24/7 access",
									"Short to medium term"
								]
							},
							{
								title: "Flexibility",
								bestFor: [
									"Moving and relocation",
									"Renovation projects"
								]
							},
							{
								title: "Easy Loading",
								bestFor: ["Direct loading", "Bulky objects"]
							}
						]
					}
				}
			]
		},
		{
			title: "Portable Storage Size Comparison",
			noPadding: true,
			modules: [
				{
					type: "size-comparison",
					props: {
						title: "How Big Is Portable Storage?",
						background: "light-blue",
						description:
							"Portable storage containers come in various sizes to match your specific needs, typically ranging from 7 feet to 20+ feet in length.",
						sizes: [
							{
								id: "7-8ft",
								label: "7-8 ft",
								title: "7-8 ft Wide",
								description:
									"* 56-64 sq. feet\n* Holds [studio or 1-bedroom apartment](https://storage.com/blog/loft-living-hacks-for-a-studio-space/) contents\n* Ideal for seasonal items and holiday decorations"
							},
							{
								id: "12-16ft",
								label: "12-16 ft",
								title: "12-16 ft Wide",
								description:
									"* 96-128 sq. feet\n* Holds the complete contents of a 2-3 bedroom home\n* Ideal for [appliances (washer, dryer, refrigerator)](https://storage.com/blog/how-do-you-store-appliances/)"
							},
							{
								id: "16-20ft",
								label: "16-20 ft",
								title: "16-20 ft Wide",
								description:
									"* 128-160 sq. feet\n* Holds the entire 3-4 bedroom home contents\n* Good option for [portable car storage (most standard vehicles)](https://storage.com/blog/the-complete-guide-to-vehicle-storage/)"
							}
						],
						postModuleDescription:
							"\n**IMPORTANT SIZING CONSIDERATION**: Most containers offer 7-8 feet of interior height, but always verify clearance requirements with your provider.\n"
					}
				}
			]
		},
		{
			title: "Portable Storage Costs",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Portable Storage Unit Costs: What to Expect",
						background: "blue",
						listType: "single",
						description:
							"Understanding portable storage unit costs helps you budget effectively and find the best deals in your area.",
						content: [
							{
								id: "1",
								value: `Here’s what you can expect to pay to rent a storage container:`
							},
							{
								id: "2",
								value: whatToExpectDescription
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Portable Storage Considerations",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Portable Storage Unit Price Considerations:",
						background: "blue",
						description: "",
						additionalContent: true,
						textAlign: "left",
						markDown: portablePriceConsiderationsRichContent,
						mediaType: "image",
						mediaUrl: portableStorageConsiderations,
						videoTitle: ""
					}
				}
			]
		},
		{
			title: "Who Needs Portable Storage",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Who Needs Portable Storage?",
						background: "light-blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "**Homeowners in Transition**\n\nMoving between homes, downsizing, or dealing with estate situations? Storage pods eliminate multiple trips to storage facilities while keeping your belongings secure and accessible."
							},
							{
								id: "2",
								value: "**Renovation and Construction Projects**\n\nKeep furniture and belongings safe during home improvements. Weatherproof storage containers protect items from dust, debris, and weather while staying conveniently on-site."
							},
							{
								id: "3",
								value: "**Military and Corporate Relocations**\n\nLong-distance moves require reliable solutions. Portable storage containers can be loaded at your origin and delivered to your destination, even across the country."
							},
							{
								id: "4",
								value: "**Seasonal and Recreational Storage**\n\nFrom holiday decorations to recreational vehicles, mini mobile storage containers adapt to your changing needs throughout the year."
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Portable Storage CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Portable",
						background: "white",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: inlineCTA,
						imageAltText: "Find portable storage"
					}
				}
			]
		},
		{
			title: "Portable Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: { ...reviewsContent, background: "blue" }
				}
			]
		},
		{
			title: "Portable Storage FAQs",
			background: "white",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Portable Storage FAQs",
						description: "",
						faqs
					}
				}
			]
		},
		{
			title: "Portable Storage Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Portable Storage",
						background: "light-blue",
						unitCards: relatedContentCards
					}
				}
			]
		},
		{
			title: "Portable Links to other PSP",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						background: "white",
						storageGridContent: {
							title: "More Ways to Store",
							image: illustrationSearch,
							storageTypes: storageCategories.portableStorage
						}
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						storageType: "portable",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPPortableContent;
